import { useState, useEffect } from 'react';
import { Trophy, Crown, Medal } from 'lucide-react';
import { apiCall } from './api';
import { getT } from './i18n';

/**
 * Leaderboard widget — top apprenants de la semaine ou all-time.
 * Reset ISO-week automatique côté backend (xp_day aggregés par semaine).
 */
export default function Leaderboard({ token, lang = 'fr' }) {
  const t = getT(lang);
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);

  useEffect(() => {
    let abort = false;
    apiCall(`/leaderboard?period=${period}`, 'GET', null, token)
      .then(r => { if (!abort) setData(r); })
      .catch(() => {});
    return () => { abort = true; };
  }, [period, token]);

  const medal = (rank) => {
    if (rank === 1) return <Crown size={14} className="text-yellow-300" />;
    if (rank === 2) return <Medal size={14} className="text-slate-300" />;
    if (rank === 3) return <Medal size={14} className="text-amber-500" />;
    return <span className="text-[10px] text-white/40 w-3.5 text-center">{rank}</span>;
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="leaderboard-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Trophy size={18} className="text-yellow-300" /><h3 className="font-bold">{t('lb_title')}</h3></div>
        <div className="flex gap-1 text-[11px]">
          <button onClick={() => setPeriod('week')} data-testid="lb-period-week"
            className={`px-2 py-0.5 rounded-full ${period==='week'?'bg-yellow-400 text-slate-950 font-bold':'bg-white/5 text-white/60'}`}>{t('lb_week')}</button>
          <button onClick={() => setPeriod('all')} data-testid="lb-period-all"
            className={`px-2 py-0.5 rounded-full ${period==='all'?'bg-yellow-400 text-slate-950 font-bold':'bg-white/5 text-white/60'}`}>{t('lb_all')}</button>
        </div>
      </div>

      {!data ? (
        <div className="text-xs text-white/40 py-4 text-center">{t('dash_loading')}</div>
      ) : (data.entries || []).length === 0 ? (
        <div className="text-xs text-white/40 py-4 text-center">{t('lb_empty')}</div>
      ) : (
        <ol className="space-y-1.5">
          {data.entries.map(e => (
            <li key={e.rank} data-testid={`lb-row-${e.rank}`}
              className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm ${e.is_me ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-white/5'}`}>
              <span className="w-5 flex justify-center">{medal(e.rank)}</span>
              <span className="flex-1 truncate font-bold">{e.name}{e.is_me && <span className="ml-1 text-[10px] text-emerald-300">· {t('lb_me')}</span>}</span>
              <span className="text-yellow-300 font-bold text-xs">{e.xp.toLocaleString()} XP</span>
            </li>
          ))}
        </ol>
      )}

      {data?.my_rank && data.my_rank > (data.entries?.length || 0) && (
        <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-white/60 flex justify-between">
          <span>{t('lb_your_rank')}</span>
          <span className="text-emerald-300 font-bold">#{data.my_rank}</span>
        </div>
      )}
    </div>
  );
}
